/**
 * What a level costs, and what an amount of experience is worth.
 *
 * The curve is linear in its increments and therefore quadratic in its total: reaching level `n`
 * from level 1 costs `XP_PER_LEVEL_STEP * n * (n - 1) / 2`. That shape was chosen over an
 * exponential one because a browser co-op game is played in twenty minute raids, and an exponential
 * tail turns the last third of the curve into a wall nobody walks. Every level here is reachable by
 * playing, and the last one costs about twice the first ten put together.
 *
 * Everything is integer arithmetic and every function is total. `levelForXp` walks the curve rather
 * than inverting it with a square root, because thirty iterations cost nothing and a floating point
 * inverse is one rounding error away from a peer disagreeing about whether someone levelled.
 */

/** The last level. Past this, experience stops accruing rather than accruing unread. */
export const LEVEL_CAP = 30;

export const FIRST_LEVEL = 1;

/**
 * What each step up the curve adds to the previous one. Level 2 costs this, level 3 costs twice it,
 * and so on, which is what makes the total quadratic.
 */
export const XP_PER_LEVEL_STEP = 120;

/** Total experience to have reached a level. Zero at level one, by construction. */
export function xpForLevel(level: number): number {
  const clamped = clampLevel(level);

  return (XP_PER_LEVEL_STEP * clamped * (clamped - 1)) / 2;
}

/** The most experience worth holding. Past the cap there is nothing left to buy with it. */
export const MAX_XP = xpForLevel(LEVEL_CAP);

/** Experience to a level, walked rather than inverted. Always in `[FIRST_LEVEL, LEVEL_CAP]`. */
export function levelForXp(xp: number): number {
  const held = clampXp(xp);
  let level = FIRST_LEVEL;

  while (level < LEVEL_CAP && held >= xpForLevel(level + 1)) level += 1;

  return level;
}

/** Experience earned since the current level began. Zero the moment a level lands. */
export function xpIntoLevel(xp: number): number {
  return clampXp(xp) - xpForLevel(levelForXp(xp));
}

/** What this level costs in total. Zero at the cap, where there is no next level to cost. */
export function xpAcrossLevel(xp: number): number {
  const level = levelForXp(xp);

  return level >= LEVEL_CAP ? 0 : xpForLevel(level + 1) - xpForLevel(level);
}

/** Experience still to find before the next level. Zero at the cap. */
export function xpToNextLevel(xp: number): number {
  const level = levelForXp(xp);

  return level >= LEVEL_CAP ? 0 : xpForLevel(level + 1) - clampXp(xp);
}

/**
 * Progress through the current level, in `[0, 1]`, for a bar to draw. One at the cap: the bar is
 * full and stays full, which reads as finished rather than as stuck.
 */
export function levelProgress(xp: number): number {
  const across = xpAcrossLevel(xp);

  return across === 0 ? 1 : xpIntoLevel(xp) / across;
}

/**
 * Pulls a raw total into the usable range. Experience arrives from saves, which this does not own.
 *
 * Only `NaN` falls back to nothing. An infinity is compared like any other number, so a record
 * holding one lands on the cap rather than on zero: a value that is unambiguously too large should
 * not read as a character who has never played.
 */
export function clampXp(xp: number): number {
  if (Number.isNaN(xp)) return 0;
  if (xp < 0) return 0;
  if (xp > MAX_XP) return MAX_XP;
  return Math.floor(xp);
}

function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return FIRST_LEVEL;
  if (level < FIRST_LEVEL) return FIRST_LEVEL;
  if (level > LEVEL_CAP) return LEVEL_CAP;
  return Math.floor(level);
}
